<script lang="ts" setup>
import type { MenuItem } from '@bloomreach/spa-sdk'
import type { PropType } from 'vue'
import { onMounted, useTemplateRef } from 'vue'
import { useHeader } from '@/cms-ui/composables/useHeader'
import { EClickElement } from '@/cms-ui/utils/dataLayer'
import { useClickTracking } from '@/cms-ui/composables/useAnalytics'

defineProps({
    isOpen: {
        type: Boolean,
        default: false,
    },
    items: {
        type: Array as PropType<MenuItem[]>,
        default: () => [],
    },
})

const { desktopDrawerActiveIndex, desktopSubmenuRefs, desktopSubmenuWrapperRef, getTarget } = useHeader()
const desktopNavWrapperRef = useTemplateRef('desktopNavWrapperRef')
const itemRefs = useTemplateRef('itemRefs')

onMounted(() => {
    desktopSubmenuWrapperRef.value = desktopNavWrapperRef.value as HTMLElement
    desktopSubmenuRefs.value = itemRefs.value as unknown as HTMLElement
})
</script>

<template>
    <nav
        id="desktop-menu-drawer"
        class="duration-400 hidden overflow-hidden text-sm text-slate-600 transition-all lg:grid"
        :class="
            isOpen
                ? 'grid-rows-[1fr] opacity-100 ease-out duration-500'
                : 'grid-rows-[0fr] opacity-0 ease-in duration-400'
        "
    >
        <div class="overflow-hidden">
            <div class="!pt-lg !pb-xl container grid grid-cols-12 px-0">
                <div
                    ref="desktopNavWrapperRef"
                    class="col-span-4 col-start-2 h-0 transition-all duration-500 ease-in-out"
                >
                    <TransitionGroup name="fade">
                        <div
                            v-for="(el, i) in items"
                            v-show="desktopDrawerActiveIndex === i"
                            :key="`drawer-${i}`"
                            ref="itemRefs"
                            class="flex min-h-[210px] flex-col justify-center"
                        >
                            <a
                                v-for="item in el.getChildren()"
                                :key="item.getName()"
                                :href="item.getUrl()"
                                :target="getTarget(item)"
                                class="text-comp-high-contrast-on-dark heading-sm p-sm mb-xs hover:underline"
                                @click="
                                    useClickTracking({
                                        label: item.getName(),
                                        clickPath: item.getUrl() || '',
                                        clickElement: EClickElement.LINK,
                                        componentName: 'header',
                                        componentCategory: 'menu',
                                    })
                                "
                            >
                                {{ item.getName() }}
                            </a>
                        </div>
                    </TransitionGroup>
                </div>
            </div>
        </div>
    </nav>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
  position: absolute;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
